import { Input } from '@/components/base/form/input';
import { Loading } from '@/components/base/loading';
import { Pagination } from '@/components/base/pagination';
import Section from '@/components/base/section';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/base/table';
import { cn } from '@/utils';
import {
  faAnglesUpDown,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useSnackbar } from 'notistack';
import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DataTableProps<T> {
  data: T[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- needed
  columns: ColumnDef<T, any>[];
  itemsPerPage?: number;
  searchPlaceholder?: string;
  onSearch?: (query: string) => T[];
  onFilter?: (data: T[], query: string) => Promise<T[]>;
  filter?: ReactNode;
  children?: ReactNode;
  onPageChange?: (page: number) => void;
  totalPages?: number;
  page?: number;
  rowClick?: (data: T) => void;
  rowClassName?: ((data: T) => string) | string;
}

export function TableWithFeatures<T>({
  data,
  columns,
  itemsPerPage = 10,
  searchPlaceholder = 'Search...',
  onFilter,
  onSearch,
  filter,
  children,
  totalPages,
  onPageChange,
  page,
  rowClick,
  rowClassName,
}: DataTableProps<T>): JSX.Element {
  const [currentPage, setCurrentPage] = useState(page ?? 0);
  const [searchQuery, setSearchQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  useEffect(() => {
    const fetchFilteredData = async () => {
      setIsLoading(true);

      let filtered = data;
      if (onSearch && searchQuery) {
        try {
          filtered = onSearch(searchQuery);
        } catch (error) {
          enqueueSnackbar({
            message: `Error fetching search results: ${String(error)}`,
            variant: 'error',
          });
        }
      }
      if (onFilter && searchQuery) {
        try {
          filtered = await onFilter(filtered, searchQuery);
        } catch (error) {
          enqueueSnackbar({
            message: `Error applying custom filter: ${String(error)}`,
            variant: 'error',
          });
        }
      }

      setFilteredData(filtered);
      setIsLoading(false);
    };

    fetchFilteredData();
  }, [data, searchQuery, onSearch, onFilter]);

  const paginatedData = useMemo(() => {
    if (onPageChange) {
      return filteredData;
    }

    const start = currentPage * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredData ? filteredData.slice(start, end) : [];
  }, [filteredData, currentPage, itemsPerPage]);

  const table = useReactTable({
    data: paginatedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const handlePageChange = ({ selected }: { selected: number }) => {
    setCurrentPage(selected);
    onPageChange && onPageChange(selected);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setCurrentPage(0);
    onPageChange && onPageChange(0);
  };

  const getSortIcon = (isSorted: string | false) => {
    if (isSorted === 'asc')
      return <FontAwesomeIcon className="h-2 w-2" icon={faChevronUp} />;
    if (isSorted === 'desc')
      return <FontAwesomeIcon className="h-2 w-2" icon={faChevronDown} />;
    return <FontAwesomeIcon className="h-2 w-2" icon={faAnglesUpDown} />;
  };

  return (
    <div>
      <div className="flex w-full flex-col items-start justify-between md:flex-row md:items-center">
        {children}
        {onSearch && (
          <div className="mb-4 flex items-center">
            <div className="w-fit pl-px">
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={handleSearchChange}
                variant="secondary"
                className="w-full max-w-60"
              />
            </div>
            {filter}
          </div>
        )}
      </div>
      <Section className="p-0 sm:p-0" variant="secondary">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                className="[&>*:first-child]:pl-5 [&>*:last-child]:pr-5"
                id={headerGroup.id}
                key={headerGroup.id}
              >
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className="group cursor-pointer px-3 py-3 sm:px-6"
                    onClick={() => {
                      header.column.getCanSort()
                        ? header.column.toggleSorting()
                        : undefined;
                    }}
                  >
                    <div className="flex items-center group-last:justify-end">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {header.column.getCanSort() && (
                        <span className="ml-1 flex items-center justify-center">
                          {getSortIcon(header.column.getIsSorted())}
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          {isLoading && <Loading />}
          <TableBody>
            {table.getRowModel().rows.map((row, id) => (
              <TableRow
                data-testid={`row-${id}`}
                className={cn(
                  'text-xs text-default-gray-400 sm:text-sm',
                  typeof rowClassName === 'function'
                    ? rowClassName(row.original)
                    : (rowClassName ?? ''),
                )}
                id={row.id}
                key={row.id}
                onClick={() => {
                  rowClick && rowClick(row.original);
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    className="whitespace-nowrap px-3 py-3 sm:px-6"
                    key={cell.id}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>

          {table.getRowModel().rows.length === 0 && (
            <TableRow className="border-none">
              <TableCell
                colSpan={columns.length}
                className="whitespace-nowrap px-3 py-3 sm:px-6"
              >
                <div className="flex w-full items-center justify-center text-default-gray-950 dark:text-white">
                  <span>{t('table.noData')}</span>
                </div>
              </TableCell>
            </TableRow>
          )}
        </Table>
      </Section>

      {totalPages ? (
        <div className="mt-4 flex w-full justify-end">
          <Pagination
            currentPage={currentPage}
            totalPages={
              totalPages ?? Math.ceil(filteredData.length / itemsPerPage)
            }
            handlePageChange={handlePageChange}
          />
        </div>
      ) : null}
    </div>
  );
}
